/**
 * Global error tracker (iter 205).
 *
 * Pipes errors to the backend `POST /api/error-logs/track` endpoint so the
 * admin's error dashboard can aggregate, filter and analyze them.
 *
 * Captures:
 *   • axios 4xx/5xx responses and network failures (response interceptor)
 *   • Uncaught JS exceptions (window.onerror)
 *   • Unhandled promise rejections (window.onunhandledrejection)
 *
 * Notes:
 *   • Skips logging requests TO /api/error-logs/track to avoid recursion.
 *   • Truncates large stack traces to ~3 KB.
 *   • Fires-and-forgets via fetch (no axios) so failed-tracker calls don't loop.
 */
import { API_URL } from './api';

const TRACK_ENDPOINT = `${API_URL}/api/error-logs/track`;
const RECENT_FINGERPRINTS = new Map(); // dedupe identical errors within 3s

function shouldSkip(message, path) {
  if (!message) return true;
  // Skip recursion safeguard
  if (path && (path.includes('/api/error-logs') || path.includes('/api/admin/error-logs'))) return true;
  // Dedup identical errors within a short window
  const fp = `${path || ''}::${message.slice(0, 120)}`;
  const now = Date.now();
  const last = RECENT_FINGERPRINTS.get(fp);
  if (last && now - last < 3000) return true;
  RECENT_FINGERPRINTS.set(fp, now);
  // Cleanup map
  if (RECENT_FINGERPRINTS.size > 200) {
    const cutoff = now - 5000;
    for (const [k, v] of RECENT_FINGERPRINTS) {
      if (v < cutoff) RECENT_FINGERPRINTS.delete(k);
    }
  }
  return false;
}

export function reportError(payload) {
  if (shouldSkip(payload.error_message, payload.path)) return;
  try {
    fetch(TRACK_ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => { /* swallow */ });
  } catch { /* swallow */ }
}

export function installErrorTracker(axiosInstance) {
  // Axios response interceptor — catches 4xx/5xx + network failures
  if (axiosInstance && axiosInstance.interceptors) {
    axiosInstance.interceptors.response.use(
      (resp) => resp,
      (err) => {
        try {
          const config = err?.config || {};
          const url = config.url || '';
          const status = err?.response?.status;
          // iter 256 — a backend middleware már naplózza a 4xx/5xx-et; a
          // frontendről csak a HÁLÓZATI hibát (nincs válasz) és a 5xx-et
          // jelentjük, a 401/403/404 (lejárt session, jogosultság, hiányzó
          // erőforrás) várt válasz, nem hiba → nem duplikáljuk a naplóban.
          if (status && status < 500) {
            return Promise.reject(err);
          }
          const detail = err?.response?.data?.detail
                       || err?.response?.data?.message
                       || err?.message
                       || 'Network error';
          const path = (() => {
            try {
              const u = new URL(url, window.location.origin);
              return u.pathname + (u.search || '');
            } catch { return url; }
          })();
          const severity = status >= 500 ? 'critical' : (status >= 400 ? 'error' : 'warning');
          reportError({
            severity,
            error_message: typeof detail === 'string' ? detail : JSON.stringify(detail).slice(0, 500),
            error_type: status ? `http_${status}` : 'network',
            status_code: status || null,
            path,
            metadata: {
              method: (config.method || 'GET').toUpperCase(),
              code: err?.code || null,
            },
          });
        } catch { /* never break app */ }
        return Promise.reject(err);
      }
    );
  }

  // Uncaught JS exceptions
  window.addEventListener('error', (e) => {
    try {
      reportError({
        severity: 'error',
        error_message: e?.message || 'Uncaught error',
        error_type: e?.error?.name || 'js_error',
        path: window.location.pathname,
        stack_trace: e?.error?.stack?.slice(0, 3000),
      });
    } catch { /* swallow */ }
  });

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (e) => {
    try {
      const reason = e?.reason;
      const message = reason?.message || reason?.toString?.() || 'Unhandled rejection';
      reportError({
        severity: 'error',
        error_message: message.slice(0, 500),
        error_type: 'unhandled_rejection',
        path: window.location.pathname,
        stack_trace: reason?.stack?.slice(0, 3000),
      });
    } catch { /* swallow */ }
  });

  // Patch toast.error to also report — done lazily to avoid circular dependency.
  // Csak a valódi (szerver/hálózat) hibákat naplózzuk; a kitöltési figyelmeztetések („kötelező", „adj meg…")
  // nem hibák, azok nem kerülnek a hibanaplóba / napi jelentésbe.
  const REAL_ERROR_RX = /sikertelen|nem sikerült|szerverhiba|hiba történt|hálózat|nincs kapcsolat|időtúllépés|timeout|network|server error|\b5\d\d\b|internal|failed|unexpected|váratlan/i;
  const VALIDATION_RX = /kötelező|add meg|adj meg|válassz|írd be|írd le|töltsd ki|legalább|érvénytelen|formátum|hiányzik|nem lehet üres|túl (hosszú|rövid|nagy)|max\.? \d|minimum|csak számot|nem egyezik|vágólap|clipboard/i;
  try {
    import('sonner').then(({ toast }) => {
      if (toast && !toast.__errorTrackerPatched) {
        const origErr = toast.error?.bind(toast);
        if (origErr) {
          toast.error = (msg, opts) => {
            try {
              const text = typeof msg === 'string' ? msg : JSON.stringify(msg).slice(0, 300);
              if (REAL_ERROR_RX.test(text) && !VALIDATION_RX.test(text)) {
                reportError({
                  severity: 'warning',
                  error_message: text,
                  error_type: 'toast_error',
                  path: window.location.pathname,
                });
              }
            } catch { /* swallow */ }
            return origErr(msg, opts);
          };
          toast.__errorTrackerPatched = true;
        }
      }
    });
  } catch { /* sonner not yet ready */ }

  // Patch console.error → forwards to original AND reports to backend.
  // Skips React's noisy dev warnings (e.g. "Warning:" prefix) by default but
  // still captures genuine error throws.
  if (!console.__errorTrackerPatched) {
    const origConsoleError = console.error.bind(console);
    console.error = (...args) => {
      try {
        const message = args
          .map((a) => {
            if (a == null) return String(a);
            if (typeof a === 'string') return a;
            if (a instanceof Error) return a.message;
            try { return JSON.stringify(a); } catch { return String(a); }
          })
          .join(' ')
          .slice(0, 1000);
        // React dev-mode warnings start with "Warning:" — log as 'warning'
        const isReactWarn = message.startsWith('Warning:');
        // Capture stack from first Error arg
        const errArg = args.find((a) => a instanceof Error);
        reportError({
          severity: isReactWarn ? 'warning' : 'error',
          error_message: message,
          error_type: isReactWarn ? 'react_warning' : 'console_error',
          path: window.location.pathname,
          stack_trace: errArg?.stack?.slice(0, 3000),
        });
      } catch { /* never break */ }
      return origConsoleError(...args);
    };
    console.__errorTrackerPatched = true;
  }
}
